/**
 * Everything any Lifekees app ever stored under the `https://lifekees.com`
 * origin — which is now the marketing origin, where third-party code runs.
 *
 * Until the origin split the password manager itself lived on lifekees.com
 * (the v3 PWA), and before it the 2016 apps did. Their storage stays in
 * returning visitors' browsers, keyed by origin, and a third-party script on
 * this origin could read every byte of it. `wipe.ts` deletes it, using exactly
 * the names below, before `third-party.ts` will load anything.
 *
 * Sources, so the list can be re-derived rather than trusted:
 *
 *  - v3 PWA (`apps/web/src`, every name ever added in git history):
 *    - IndexedDB `lifekees` (`lib/db.ts` `DB_NAME`): encrypted vault blobs,
 *      rollback floors, email-shape records, the outbox of unsynced writes and
 *      `secretKeys` — a Secret Key the user chose to remember, **in the
 *      clear**. No other database name was ever opened outside the tests.
 *    - Cache Storage `lifekees-shell-<build id>` (`lib/sw-policy.ts`).
 *    - localStorage `lk.lastEmail`, `lk.idleMinutes`, `lk.firstRun`,
 *      `lk.passkeyHere.<email>`, `lk.passkeyHint.<email>`,
 *      `lk.passkeyCred.<email>`, `lk.passkeyAutoQuiet.<email>`.
 *    - sessionStorage `lk.token` (a 24 h bearer token) and `lk:pending-signup`.
 *    - The service worker `/sw.js`, scope `/` (`lib/sw-register.ts`); the
 *      kill switch at `site/public/sw.js` replaces it.
 *    - No cookies (the API sets none), no OPFS, no `storage.persist`.
 *  - The 2016 apps (`archive/lkgo/www/js/main.js`, `archive/lkcore`,
 *    `archive/lkwebapp`, `archive/lifekees/www/webapp`), localStorage only:
 *    `email`; the address itself as a key, holding the account's siid;
 *    `pattern_<email>` and `pattern_visible<email>`; `bio_<email>`;
 *    `biomutex`; `locked`; `scannercache`; `lk.json` (offline items, through
 *    `StoragePorts.save`). `bio_<email>` is the account's 2016 key pair
 *    encrypted under a key derived from the email address alone — effectively
 *    plaintext — and that pair's `remote` half is the credential v3's legacy
 *    login (`POST /api/accounts/login`) accepts for an unmigrated account.
 *
 * RESERVED NAMES. Marketing-site code, and any analytics configuration added
 * later, must never store anything under a name these rules match: the wipe
 * runs on every page load and would delete it. `test/old-origin-inventory.test.ts`
 * checks the app's real constants against these rules, and that nothing in
 * `apps/web/src` stores under a name outside them.
 *
 * Imports nothing: it is part of the marketing bundle, which may contain none
 * of the app's code.
 */

/** IndexedDB databases to delete. */
export const OLD_IDB_NAMES: readonly string[] = ['lifekees'];

/** Cache Storage names starting with these are the old app shell. */
export const OLD_CACHE_PREFIXES: readonly string[] = ['lifekees-shell-'];

/** Web Storage keys (local and session) starting with these. */
export const OLD_KEY_PREFIXES: readonly string[] = ['lk.', 'lk:', 'pattern_', 'bio_'];

/** Web Storage keys (local and session) with exactly these names. */
export const OLD_KEY_NAMES: readonly string[] = ['email', 'biomutex', 'locked', 'scannercache'];

/**
 * The 2016 apps kept `localStorage[<email address>] = siid`. Any key that is a
 * bare address is one of those: nothing on this origin has a reason to use an
 * address as a key name.
 */
const BARE_ADDRESS = /^[^\s@]+@[^\s@]+$/;

/** Whether a Web Storage key is one the old apps wrote. */
export function isOldStorageKey(key: string): boolean {
  if (OLD_KEY_NAMES.includes(key)) return true;
  if (OLD_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) return true;
  return BARE_ADDRESS.test(key);
}

/** Whether a Cache Storage name is the old app's shell. */
export function isOldCacheName(name: string): boolean {
  return OLD_CACHE_PREFIXES.some((prefix) => name.startsWith(prefix));
}
